/**
 * Every JSON-LD block the site emits is built here, so the sitewide entities
 * are defined once and pages reference them by @id instead of restating them.
 *
 * Before this, _document.js emitted one set and pages/blog/[id].jsx emitted a
 * second copy of the same two blocks, so blog posts carried each entity twice.
 */

export const SITE_URL = 'https://www.24hrdoc.com';
const LOGO_URL = `${SITE_URL}/assets/icons/LogoTransparent.png`;

const SITE_DESCRIPTION =
  'Explore 24/7 Online Doctor Consultations, Medical Services, and Health Assessments. No insurance? Access Virtual Medical Centers and certified care anytime.';

const SAME_AS = [
  'https://www.facebook.com/people/24-HR-DOC/100091804954061/',
  'https://www.instagram.com/24hrdoc_/',
  'https://x.com/24HRDoc1',
  'https://www.youtube.com/channel/UCYxLi6Vy_afwIda8HA_hV8Q',
];

/**
 * The sitewide entity was typed DiagnosticLab, which means a pathology lab —
 * not what 24hrDoc is. MedicalOrganization is DiagnosticLab's immediate
 * parent: the narrowest type that is still true, so it is the smallest
 * correct change.
 *
 * Deliberately not MedicalBusiness: that is a LocalBusiness subtype and
 * expects a street address and opening hours for premises patients visit,
 * which a telehealth service does not have. Plain 'Organization' also works
 * if the SEO team would rather be conservative — change this one constant.
 */
const ORGANIZATION_TYPE = 'MedicalOrganization';

// Stable node ids so pages can point at these entities without repeating them.
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

/** Wraps a schema object for <script dangerouslySetInnerHTML>. */
export function jsonLd(schema) {
  return { __html: JSON.stringify(schema) };
}

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ORGANIZATION_TYPE,
    '@id': ORGANIZATION_ID,
    name: '24hrdoc',
    url: `${SITE_URL}/`,
    logo: LOGO_URL,
    image: LOGO_URL,
    description: SITE_DESCRIPTION,
    sameAs: SAME_AS,
  };
}

/**
 * No potentialAction/SearchAction: Google retired the sitelinks search box on
 * 21 Nov 2024 and removed the documentation, so it renders nothing. The
 * target was also a fragment URL (…/services#mainSection{search_term_string}),
 * which could never have worked. WebSite itself is still supported and is
 * what drives the site name in results, so it stays.
 */
export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: '24HrDOC',
    url: `${SITE_URL}/`,
    description: SITE_DESCRIPTION,
    publisher: { '@id': ORGANIZATION_ID },
  };
}

/**
 * Page-specific, unlike the previous block which hardcoded the homepage URL
 * on every page in the site.
 */
export function medicalWebPageSchema({ name, url, description, breadcrumbId }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${url}#webpage`,
    name,
    url,
    ...(description ? { description } : {}),
    isPartOf: { '@id': WEBSITE_ID },
    publisher: { '@id': ORGANIZATION_ID },
    ...(breadcrumbId ? { breadcrumb: { '@id': breadcrumbId } } : {}),
  };
}

/**
 * Two-level trail (Home > Page). The category rung is deliberately absent:
 * condition URLs are /[category]/[slug] but the category level has no page —
 * /urgent-care, /mens-health and /dermatology all 404 — so a three-level
 * trail would point its middle rung at a dead URL.
 *
 * `item` is omitted on the final crumb, per Google's guidance that the
 * current page needs no URL.
 */
export function breadcrumbSchema(trail, pageUrl) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}#breadcrumb`,
    itemListElement: trail.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      ...(index < trail.length - 1 && crumb.url ? { item: crumb.url } : {}),
    })),
  };
}

/**
 * Service + Offer rather than Product + Offer: these are medical services at
 * a fixed price, not goods.
 *
 * No aggregateRating. The "4.2 out of 5" on condition pages is hardcoded in
 * the template, has no review count, and is 24hrDoc's overall Trustpilot
 * score rather than a rating of the individual treatment — marking it up per
 * condition would misrepresent it.
 */
export function serviceSchema({ name, description, url, price, image }) {
  const numericPrice = Number(price);
  const hasPrice = price != null && price !== '' && !Number.isNaN(numericPrice);

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${url}#service`,
    name,
    serviceType: name,
    ...(description ? { description } : {}),
    url,
    ...(image ? { image } : {}),
    provider: { '@id': ORGANIZATION_ID },
    areaServed: { '@type': 'Country', name: 'United States' },
    ...(hasPrice
      ? {
          offers: {
            '@type': 'Offer',
            price: numericPrice.toFixed(2),
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            url,
          },
        }
      : {}),
  };
}

/**
 * author falls back to the organisation only when the CMS author field is
 * empty. That fallback is valid markup but weaker for health content, so the
 * aim is for every post to carry a named Person.
 */
export function blogPostingSchema({
  url,
  headline,
  description,
  image,
  authorName,
  datePublished,
  dateModified,
  articleBody,
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    headline,
    ...(description ? { description } : {}),
    ...(image ? { image } : {}),
    author: authorName
      ? { '@type': 'Person', name: authorName }
      : { '@id': ORGANIZATION_ID },
    publisher: { '@id': ORGANIZATION_ID },
    ...(datePublished ? { datePublished } : {}),
    // Falls back to datePublished so the property is never emitted empty.
    ...(dateModified || datePublished
      ? { dateModified: dateModified || datePublished }
      : {}),
    ...(articleBody ? { articleBody } : {}),
  };
}
